import type { ItemFieldRow } from './item-field-reader';
import { createStatusLayerCompiler } from './status-layering-context';
import type { StatusLayeringDependencies } from './status-layering-context';
import { createIntegerRollupCompiler } from './integer-layer-rollup';
import type { IntegerRollupDependencies } from './integer-layer-rollup';
import { createInventoryMagicCompiler } from './inventory-magic-signature';
import type { InventoryMagicDependencies } from './inventory-magic-signature';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { utf8Bytes } from './php-json-serialize';
import { integerCompileWorkingRow,integerCompileResult,putIntegerCompilePath } from './integer-compute-slots';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown,key: string): unknown => arrayLike(value) && Object.prototype.hasOwnProperty.call(value,key)
    ? (value as Readonly<Record<string,unknown>>)[key] : undefined;
function keyed(value: unknown,key: unknown): unknown {
    if (typeof value === 'string') {
        if (arrayLike(key)) throw new TypeError('Illegal PHP string offset');
        if (typeof key === 'string' && !/^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(key)) return undefined;
        const bytes = utf8Bytes(value),offset = Number(phpJsonInteger(key));
        const byte = bytes[offset < 0 ? bytes.length + offset : offset];
        return byte === undefined ? undefined : String.fromCharCode(byte);
    }
    if (!arrayLike(value)) return undefined;
    if (arrayLike(key)) throw new TypeError('Illegal PHP array offset');
    const name = key == null ? '' : typeof key === 'number' || typeof key === 'boolean' ? String(phpJsonInteger(key)) : String(key);
    return own(value,name);
}
export class MissingInventoryStatusColumn extends Error {
    constructor(public readonly column: string) { super('Missing inventory status column: '+column); this.name = 'MissingInventoryStatusColumn'; }
}
export interface InventoryStatusCompilationDependencies {
    statuses: StatusLayeringDependencies;
    rollup: IntegerRollupDependencies;
    magic: InventoryMagicDependencies;
    /** Supply the same guard as the constructor prefix and request preparation. */
    inventoryContext?: InventoryReadContext;
}
/** Actual contiguous constructor target/caster status, light rollup and magic
 * signature section. Prefetch keys use the raw constructor ID without case
 * normalization. The two status calls see the same original prefetch value.
 * Missing input aborts the section; no later rollup or signature is fabricated.
 */
export function createInventoryStatusCompilation(deps: InventoryStatusCompilationDependencies):
    (row: ItemFieldRow,context?: ItemFieldRow) => ItemFieldRow {
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id=>{
        deps.statuses.trace?.(['inventory',id]); return deps.statuses.resolveInventoryInstance(id);
    });
    const statuses = createStatusLayerCompiler({...deps.statuses,inventoryContext});
    const rollup = createIntegerRollupCompiler({...deps.rollup,inventoryContext});
    const magic = createInventoryMagicCompiler({...deps.magic,inventoryContext});
    return (input,context=[])=>{
        if (!arrayLike(input) || !arrayLike(context)) throw new TypeError('Inventory status compilation requires PHP arrays');
        if (!Object.prototype.hasOwnProperty.call(input,'inventory_id')) throw new MissingInventoryStatusColumn('inventory_id');
        const id = own(input,'inventory_id'),prefetched = own(context,'prefetched_statuses') ?? null;
        const rows = (mode: string): unknown => phpJsonTruthy(prefetched) ? keyed(own(prefetched,mode),id) ?? [] : null;
        let row = statuses(input,'target',rows('target'));
        row = statuses(row,'caster',rows('caster'));
        row = rollup.light(row);
        const signature = magic(row);
        if (signature !== null) {
            const out = integerCompileWorkingRow(row);
            putIntegerCompilePath(out,['computed','spellsight_signature'],signature);
            row = integerCompileResult(out);
        }
        return row;
    };
}
